import { useNexus } from "src/hooks/useNexus";
import { useSelector } from "react-redux";
import { KEY, ONBOARDING_GROUP_FLAGS } from "src/consts/nexus-keys";

export type OnboardingGroupFlag = typeof ONBOARDING_GROUP_FLAGS[keyof typeof ONBOARDING_GROUP_FLAGS];
export type EndpointToggleFlag = OnboardingGroupFlag | typeof KEY.NEW_ITR_FLOW | typeof KEY.ITR_MAKER_APPROVER_FLOW;

// Nexus flag keys whose payload follows the flat { endpoint: { [group]: { actions, enable } } } shape.
type FlagEndpointKey = typeof KEY.FLAG_ENDPOINT_TRX_MICROSERVICES | typeof KEY.FLAG_ENDPOINT_ITR;

interface useGatewayProps {
  isCompanyEndpointMicroservice: (service: string, action: string) => boolean;
  isEndpointMicroservice: (flagKey: EndpointToggleFlag) => boolean;
  isCompanyEndpointItr: (service: string, action: string) => boolean;
  isNexusReady: boolean;
  isNexusStoreReady: boolean;
}

const useGateway = (): useGatewayProps => {
  const { getNexusPayload, ready: isNexusStoreReady } = useNexus();
  const user = useSelector((state: any) => state.commonUser.user);

  // Gateway is ready when BOTH Nexus store is initialized AND user data is available
  const isNexusReady = isNexusStoreReady && !!user?.company?.id;

  /**
   * Shared lookup for the flat per-group microservice flags (trx & itr).
   * Reads the flag's `endpoint -> group -> { actions, enable }` map (see ENDPOINTS_*_FLAG in
   * src/consts/api.js), searches ALL groups that contain the action, and returns true if any
   * matching group's `enable` list whitelists the company ("all" => everyone).
   * IMPORTANT: returns false until Nexus store AND user data are ready, to avoid hitting the new API early.
   */
  const isCompanyEnabledForFlag = (flagKey: FlagEndpointKey, action: string): boolean => {
    // Wait for both Nexus store AND user data to be ready
    if (!isNexusStoreReady) {
      return false;
    }

    if (!user?.company?.id) {
      return false;
    }

    const companyId = `${user?.company?.id}`;

    const endpoint = (getNexusPayload(flagKey)?.endpoint as Record<string, any>) || {};
    const matchingGroups = Object.values(endpoint).filter(
      (group: any) => Array.isArray(group?.actions) && group.actions.includes(action)
    ) as any[];

    return matchingGroups.some((group) => {
      if (!Array.isArray(group?.enable)) return false;
      if (group.enable.includes("all")) return true;
      return group.enable.includes(companyId);
    });
  };

  /**
   * Whether the company is enabled for an action of `dis-flag-endpoint-trx-microservices`.
   * Every action of this flag has been rolled out to all merchants and its call sites were collapsed to the new
   * API, so the only action still read through it is `get_dashboard_pending_transaction` (Home pending panel).
   * @param {string} _service - Kept for caller compatibility; the flat flag is searched by action.
   * @param {string} action - Action name (only 'get_dashboard_pending_transaction' remains)
   */
  const isCompanyEndpointMicroservice = (_service: string, action: string) =>
    isCompanyEnabledForFlag(KEY.FLAG_ENDPOINT_TRX_MICROSERVICES, action);

  /**
   * Whether the company is enabled for the international-transfer microservice
   * (`dis-flag-endpoint-itr`).
   * @param {string} _service - Kept for caller compatibility; the flat flag is searched by action.
   * @param {string} action - ITR action name (e.g., 'itr_get_transaction_list', 'itr_get_banks')
   */
  const isCompanyEndpointItr = (_service: string, action: string) =>
    isCompanyEnabledForFlag(KEY.FLAG_ENDPOINT_ITR, action);

  /**
   * Check whether a specific onboarding endpoint should hit the new microservice.
   * Each endpoint has an independent Nexus flag with a `{ toggle: "on" | "off" }` payload.
   * Returns false (legacy) until the Nexus store is ready AND the flag toggle is "on".
   */
  const isEndpointMicroservice = (flagKey: EndpointToggleFlag): boolean => {
    if (!isNexusStoreReady) return false;
    return getNexusPayload(flagKey)?.toggle === "on";
  };

  return {
    isCompanyEndpointMicroservice,
    isEndpointMicroservice,
    isCompanyEndpointItr,
    isNexusReady,
    isNexusStoreReady
  };
};

export default useGateway;
